import React from 'react';
import { useDarkMode } from '../contexts/DarkModeContext';

const PrivacyPolicy = () => {
  const { isDarkMode } = useDarkMode();

  return (
    <div className={`min-h-screen transition-colors duration-500 ${
      isDarkMode ? 'bg-[#0A0A0A] text-white' : 'bg-white text-gray-900'
    }`}>
      {/* Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {/* Dark mode pattern */}
        <div className={`absolute inset-0 ${!isDarkMode && 'hidden'}`}
             style={{
               backgroundImage: 'url("/images/widebgpattern.png")',
               backgroundSize: 'cover',
               backgroundAttachment: 'fixed',
               opacity: 0.07,
               mixBlendMode: 'lighten'
             }}>
        </div>
        
        {/* Light mode pattern with inversion technique */}
        <div className={`absolute inset-0 ${isDarkMode && 'hidden'}`}
             style={{
               backgroundImage: 'url("/images/widebgpattern.png")',
               backgroundSize: 'cover',
               backgroundAttachment: 'fixed',
               opacity: 0.07,
               filter: 'invert(1)',
               mixBlendMode: 'multiply'
             }}>
        </div>
      </div>

      {/* Content Section */}
      <div className="relative z-10">
        <div className="max-w-3xl mx-auto px-8 py-16">
          <h1 className="text-3xl font-serif font-semibold mb-8 text-emerald-900 dark:text-white">Privacy Policy</h1>
          
          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">1. Introduction</h2>
              <p className="mb-3">This Privacy Policy explains how Ummahti ("we," "our," or "us") collects, uses, and protects your personal data in accordance with the General Data Protection Regulation (GDPR) and other applicable privacy laws.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">2. Data Controller</h2>
              <p className="mb-3">Ummahti is the data controller responsible for your personal data. For any privacy-related questions, please contact us at privacy@ummahti.net.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">3. Information We Collect</h2>
              <p className="mb-3">We collect and process the following types of information:</p>
              <ul className="list-disc pl-6 mb-3">
                <li>Account information (email, name, profile picture)</li>
                <li>Location data (for prayer times and qibla direction)</li>
                <li>Device information (device ID, operating system)</li>
                <li>Usage data (app interactions, preferences)</li>
                <li>Prayer and religious preferences</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">4. Legal Basis for Processing</h2>
              <p className="mb-3">We process your data based on:</p>
              <ul className="list-disc pl-6 mb-3">
                <li>Your consent</li>
                <li>Performance of our contract with you</li>
                <li>Our legitimate interests</li>
                <li>Legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">5. How We Use Your Information</h2>
              <p className="mb-3">We use your information to:</p>
              <ul className="list-disc pl-6 mb-3">
                <li>Provide accurate prayer times and qibla direction</li>
                <li>Personalize your app experience</li>
                <li>Send prayer time notifications</li>
                <li>Improve our services</li>
                <li>Communicate with you about our services</li>
                <li>Ensure app security and prevent fraud</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">6. Data Sharing and Third Parties</h2>
              <p className="mb-3">We share your data with:</p>
              <ul className="list-disc pl-6 mb-3">
                <li>Service providers (hosting, analytics)</li>
                <li>Authentication providers (Google)</li>
                <li>Third-party integrations (when you enable them)</li>
              </ul>
              <p className="mb-3">We ensure all third parties respect the security of your data and treat it in accordance with the law.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">7. International Transfers</h2>
              <p className="mb-3">When we transfer data outside the EEA, we ensure appropriate safeguards are in place through standard contractual clauses or other legal mechanisms.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">8. Data Security</h2>
              <p className="mb-3">We implement appropriate technical and organizational measures to protect your data, including encryption and secure data storage.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">9. Your Rights</h2>
              <p className="mb-3">Under GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 mb-3">
                <li>Access your personal data</li>
                <li>Rectify inaccurate data</li>
                <li>Request erasure of your data</li>
                <li>Restrict or object to processing</li>
                <li>Data portability</li>
                <li>Withdraw consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">10. Data Retention</h2>
              <p className="mb-3">We retain your data only for as long as necessary to provide our services or comply with legal obligations.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">11. Children's Privacy</h2>
              <p className="mb-3">Our services are not intended for children under 13. We do not knowingly collect data from children under 13.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">12. Cookies and Tracking</h2>
              <p className="mb-3">We use cookies and similar technologies to improve your experience. You can control these through your browser settings.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">13. Changes to This Policy</h2>
              <p className="mb-3">We may update this policy periodically. We will notify you of any significant changes.</p>
            </section>

            <section>
              <h2 className="text-xl font-serif font-semibold mb-3 text-emerald-900 dark:text-white">14. Contact Us</h2>
              <p className="mb-3">For any privacy-related questions or to exercise your rights, please contact us at:</p>
              <p className="mb-3">
                Email: privacy@ummahti.net
              </p>
              <p className="mb-3">You have the right to lodge a complaint with your local data protection authority.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 